import React, { useMemo, useState, useEffect } from "react";
import { Layout } from "@/components/Layout";
import { useListRecipes, useBulkUpdateStatus, useBulkDeleteRecipes } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Search,
  LayoutGrid,
  List,
  CheckSquare,
  Square,
  ChevronDown,
  CheckCircle2,
  AlertTriangle,
  Clock,
  Trash2,
  Download,
  Filter,
  X,
  BookOpen,
  Wand2,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

type SortKey = "title" | "date" | "category";
type ViewMode = "grid" | "table";

const STATUS_COLORS: Record<string, string> = {
  validated: "bg-green-100 text-green-800 border-green-200",
  needs_correction: "bg-orange-100 text-orange-800 border-orange-200",
  unreviewed: "bg-gray-100 text-gray-700 border-gray-200",
};
const STATUS_LABELS: Record<string, string> = {
  validated: "Validated",
  needs_correction: "Needs Correction",
  unreviewed: "Unreviewed",
};

function getBaseUrl(): string {
  return import.meta.env.BASE_URL?.replace(/\/$/, "") ?? "";
}

interface NutritionSummary {
  recipeId: number;
  perServing: { calories?: number } | null;
}

function useNutritionSummaries(): Map<number, number> {
  const [calorieMap, setCalorieMap] = useState<Map<number, number>>(new Map());
  useEffect(() => {
    fetch(`${getBaseUrl()}/api/nutrition/summaries`)
      .then((r) => r.json() as Promise<{ summaries: NutritionSummary[] }>)
      .then(({ summaries }) => {
        const m = new Map<number, number>();
        for (const s of summaries) {
          const cal = s.perServing?.calories;
          if (cal != null) m.set(s.recipeId, Math.round(cal));
        }
        setCalorieMap(m);
      })
      .catch(() => {});
  }, []);
  return calorieMap;
}

function triggerExportDownload(filter: string, ids?: number[], categories?: string[]) {
  const base = getBaseUrl();
  const params = new URLSearchParams({ filter });
  if (ids && ids.length > 0) params.set("ids", ids.join(","));
  if (categories && categories.length > 0) params.set("categories", categories.join(","));
  window.open(`${base}/api/export/excel?${params.toString()}`, "_blank");
}

export default function RecipesPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();
  const calorieMap = useNutritionSummaries();

  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterCategories, setFilterCategories] = useState<string[]>([]);
  const [filterSourceType, setFilterSourceType] = useState<string>("all");
  const [filterCuisine, setFilterCuisine] = useState<string>("all");
  const [filterHasNotes, setFilterHasNotes] = useState<string>("all");
  const [sortKey, setSortKey] = useState<SortKey>("title");
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(60);
  const PAGE_SIZE = 60;

  const { data, isLoading } = useListRecipes();
  const recipes = data?.recipes ?? [];

  const allCategories = useMemo(
    () =>
      [...new Set(recipes.map((r) => r.category).filter(Boolean) as string[])].sort(),
    [recipes]
  );
  const allSourceTypes = useMemo(
    () =>
      [...new Set(recipes.map((r) => r.sourceType).filter(Boolean) as string[])].sort(),
    [recipes]
  );
  const allCuisines = useMemo(
    () =>
      [...new Set(recipes.map((r) => r.cuisine).filter(Boolean) as string[])].sort(),
    [recipes]
  );

  // Reset pagination when filters/sort/search change
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [
    search,
    filterStatus,
    filterCategories,
    filterSourceType,
    filterCuisine,
    filterHasNotes,
    sortKey,
    viewMode,
  ]);

  const filtered = useMemo(() => {
    let list = [...recipes];
    const q = search.toLowerCase().trim();
    if (q) {
      list = list.filter((r) => {
        const ingText = (r.ingredients ?? []).map((i) => i.item).join(" ");
        return (
          r.title.toLowerCase().includes(q) ||
          (r.authorSource ?? "").toLowerCase().includes(q) ||
          (r.handwrittenNotes ?? "").toLowerCase().includes(q) ||
          (r.transcriberNotes ?? "").toLowerCase().includes(q) ||
          ingText.toLowerCase().includes(q)
        );
      });
    }
    if (filterStatus !== "all") {
      list = list.filter((r) => r.validationStatus === filterStatus);
    }
    if (filterCategories.length > 0) {
      list = list.filter((r) => r.category && filterCategories.includes(r.category));
    }
    if (filterSourceType !== "all") {
      list = list.filter((r) => r.sourceType === filterSourceType);
    }
    if (filterCuisine !== "all") {
      list = list.filter((r) => r.cuisine === filterCuisine);
    }
    if (filterHasNotes === "yes") {
      list = list.filter((r) => r.handwrittenNotes?.trim());
    } else if (filterHasNotes === "no") {
      list = list.filter((r) => !r.handwrittenNotes?.trim());
    }
    list.sort((a, b) => {
      if (sortKey === "title") return a.title.localeCompare(b.title);
      if (sortKey === "category")
        return (a.category ?? "").localeCompare(b.category ?? "");
      // date — newest first
      return (
        new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime()
      );
    });
    return list;
  }, [
    recipes,
    search,
    filterStatus,
    filterCategories,
    filterSourceType,
    filterCuisine,
    filterHasNotes,
    sortKey,
  ]);

  const bulkStatusMut = useBulkUpdateStatus({
    mutation: {
      onSuccess: (_, vars) => {
        toast({ title: "Status updated", description: `${vars.data.ids.length} recipe(s) updated.` });
        queryClient.invalidateQueries({ queryKey: ["listRecipes"] });
        setSelected(new Set());
      },
    },
  });

  const bulkDeleteMut = useBulkDeleteRecipes({
    mutation: {
      onSuccess: (_, vars) => {
        toast({
          title: "Recipes deleted",
          description: `${vars.data.ids.length} recipe(s) deleted.`,
        });
        queryClient.invalidateQueries({ queryKey: ["listRecipes"] });
        setSelected(new Set());
        setShowDeleteConfirm(false);
      },
    },
  });

  const toggleSelect = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map((r) => r.id)));
    }
  };

  const handleBulkStatus = (status: string) => {
    bulkStatusMut.mutate({ data: { ids: [...selected], validationStatus: status } });
  };

  const handleBulkDelete = () => {
    bulkDeleteMut.mutate({ data: { ids: [...selected] } });
  };

  const handleBulkExport = () => {
    triggerExportDownload("selected", [...selected]);
  };

  const [reextracting, setReextracting] = useState(false);
  const handleBulkReextract = async (mode: "overwrite" | "candidate") => {
    const ids = [...selected];
    if (ids.length === 0) return;
    if (
      mode === "overwrite" &&
      !window.confirm(
        `Overwrite ${ids.length} recipe(s) by re-running AI extraction? This cannot be undone.`,
      )
    ) {
      return;
    }
    setReextracting(true);
    try {
      const r = await fetch(`${getBaseUrl()}/api/reextract`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mode,
          filter: { type: "selectedRecipes", recipeIds: ids },
        }),
      });
      const data = (await r.json()) as {
        queued?: number;
        alreadyInFlight?: number;
        error?: string;
        total?: number;
      };
      if (!r.ok) {
        toast({
          title: "Couldn't start re-extraction",
          description: data.error ?? "Unknown error",
          variant: "destructive",
        });
        return;
      }
      toast({
        title:
          mode === "overwrite"
            ? "Overwrite re-extraction queued"
            : "Candidate re-extraction queued",
        description: `${data.queued ?? 0} queued${
          data.alreadyInFlight ? ` · ${data.alreadyInFlight} already in flight` : ""
        }. Check the Dashboard for progress${
          mode === "candidate" ? " and to review candidates" : ""
        }.`,
      });
      setSelected(new Set());
    } catch (err) {
      toast({
        title: "Network error",
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      });
    } finally {
      setReextracting(false);
    }
  };

  const toggleCategory = (cat: string) => {
    setFilterCategories((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]
    );
  };

  const clearFilters = () => {
    setSearch("");
    setFilterStatus("all");
    setFilterCategories([]);
    setFilterSourceType("all");
    setFilterCuisine("all");
    setFilterHasNotes("all");
  };

  const hasActiveFilters =
    search ||
    filterStatus !== "all" ||
    filterCategories.length > 0 ||
    filterSourceType !== "all" ||
    filterCuisine !== "all" ||
    filterHasNotes !== "all";

  if (isLoading) {
    return (
      <Layout>
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="space-y-2">
              <div className="h-8 w-56 rounded-xl bg-muted animate-pulse" />
              <div className="h-4 w-40 rounded-full bg-muted animate-pulse" />
            </div>
            <div className="h-9 w-28 rounded-md bg-muted animate-pulse" />
          </div>
          <div className="h-10 w-full rounded-md bg-muted animate-pulse" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="rounded-2xl border bg-card p-4 space-y-3">
                <div className="h-4 w-3/4 rounded-full bg-muted animate-pulse" />
                <div className="flex gap-1.5">
                  <div className="h-5 w-16 rounded-full bg-muted animate-pulse" />
                  <div className="h-5 w-14 rounded-full bg-muted animate-pulse" />
                </div>
                <div className="space-y-1.5">
                  <div className="h-3 w-1/2 rounded-full bg-muted animate-pulse" />
                  <div className="h-3 w-1/3 rounded-full bg-muted animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-3xl font-serif font-bold text-foreground">Recipe Library</h2>
            <p className="text-muted-foreground mt-1">
              {recipes.length} recipe{recipes.length !== 1 ? "s" : ""} in your collection
            </p>
          </div>
          <Button
            variant="outline"
            className="gap-2"
            onClick={() => triggerExportDownload("all")}
          >
            <Download className="w-4 h-4" />
            Export All
          </Button>
        </div>

        {/* Search + Controls bar */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search title, author, ingredients, notes…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="flex gap-2 shrink-0">
            <Button
              variant="outline"
              size="sm"
              className="gap-2"
              onClick={() => setFiltersOpen((v) => !v)}
            >
              <Filter className="w-4 h-4" />
              Filters
              {hasActiveFilters && (
                <span className="bg-primary text-primary-foreground rounded-full text-xs px-1.5 py-0.5 leading-none">
                  !
                </span>
              )}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  Sort: {sortKey === "title" ? "A-Z" : sortKey === "date" ? "Date" : "Category"}
                  <ChevronDown className="w-3 h-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => setSortKey("title")}>
                  Title A-Z
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSortKey("date")}>
                  Date Added
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSortKey("category")}>
                  Category
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <div className="flex border rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode("grid")}
                className={cn(
                  "px-2 py-1.5 transition-colors",
                  viewMode === "grid"
                    ? "bg-primary text-primary-foreground"
                    : "hover:bg-muted"
                )}
                title="Card grid"
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode("table")}
                className={cn(
                  "px-2 py-1.5 transition-colors",
                  viewMode === "table"
                    ? "bg-primary text-primary-foreground"
                    : "hover:bg-muted"
                )}
                title="Table view"
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Filters panel */}
        {filtersOpen && (
          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Status */}
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">
                    Validation Status
                  </label>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                  >
                    <option value="all">All statuses</option>
                    <option value="unreviewed">Unreviewed</option>
                    <option value="validated">Validated</option>
                    <option value="needs_correction">Needs Correction</option>
                  </select>
                </div>
                {/* Source Type */}
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">
                    Source Type
                  </label>
                  <select
                    value={filterSourceType}
                    onChange={(e) => setFilterSourceType(e.target.value)}
                    className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                  >
                    <option value="all">All types</option>
                    {allSourceTypes.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
                {/* Cuisine */}
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">
                    Cuisine
                  </label>
                  <select
                    value={filterCuisine}
                    onChange={(e) => setFilterCuisine(e.target.value)}
                    className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                  >
                    <option value="all">All cuisines</option>
                    {allCuisines.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
                {/* Has Handwritten Notes */}
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">
                    Handwritten Notes
                  </label>
                  <select
                    value={filterHasNotes}
                    onChange={(e) => setFilterHasNotes(e.target.value)}
                    className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                  >
                    <option value="all">Any</option>
                    <option value="yes">Has notes</option>
                    <option value="no">No notes</option>
                  </select>
                </div>
              </div>
              {/* Category multi-select */}
              {allCategories.length > 0 && (
                <div className="mt-4 space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">
                    Categories (select any)
                  </label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {allCategories.map((cat) => (
                      <button
                        key={cat}
                        onClick={() => toggleCategory(cat)}
                        className={cn(
                          "text-xs px-3 py-1 rounded-full border transition-colors",
                          filterCategories.includes(cat)
                            ? "bg-primary text-primary-foreground border-primary"
                            : "bg-background border-border hover:bg-muted"
                        )}
                      >
                        {cat}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-3 gap-1 text-muted-foreground"
                  onClick={clearFilters}
                >
                  <X className="w-3 h-3" />
                  Clear all filters
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Bulk action bar */}
        {selected.size > 0 && (
          <div className="flex flex-wrap items-center gap-3 bg-primary/5 border border-primary/20 rounded-xl px-4 py-3">
            <span className="text-sm font-medium text-primary">
              {selected.size} selected
            </span>
            <div className="flex flex-wrap gap-2 ml-auto">
              <Button
                size="sm"
                variant="outline"
                className="gap-1"
                onClick={() => handleBulkStatus("validated")}
                disabled={bulkStatusMut.isPending}
              >
                <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
                Mark Validated
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="gap-1"
                onClick={() => handleBulkStatus("needs_correction")}
                disabled={bulkStatusMut.isPending}
              >
                <AlertTriangle className="w-3.5 h-3.5 text-orange-500" />
                Needs Correction
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="gap-1"
                onClick={() => handleBulkStatus("unreviewed")}
                disabled={bulkStatusMut.isPending}
              >
                <Clock className="w-3.5 h-3.5 text-gray-500" />
                Mark Unreviewed
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="gap-1"
                onClick={handleBulkExport}
              >
                <Download className="w-3.5 h-3.5" />
                Export Selected
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1 border-purple-300 text-purple-700 hover:bg-purple-50"
                    disabled={reextracting}
                  >
                    <Wand2 className="w-3.5 h-3.5" />
                    Re-extract
                    <ChevronDown className="w-3 h-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => handleBulkReextract("candidate")}>
                    Create candidates (review later)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleBulkReextract("overwrite")}>
                    Overwrite in place
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                size="sm"
                variant="outline"
                className="gap-1 border-destructive/40 text-destructive hover:bg-destructive/5"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={bulkDeleteMut.isPending}
              >
                <Trash2 className="w-3.5 h-3.5" />
                Delete
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setSelected(new Set())}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Select all row */}
        {filtered.length > 0 && (
          <div className="flex items-center gap-3 text-sm text-muted-foreground flex-wrap">
            <button
              onClick={toggleSelectAll}
              className="flex items-center gap-1.5 hover:text-foreground transition-colors"
            >
              {selected.size === filtered.length && filtered.length > 0 ? (
                <CheckSquare className="w-4 h-4 text-primary" />
              ) : (
                <Square className="w-4 h-4" />
              )}
              {selected.size === filtered.length && filtered.length > 0
                ? "Deselect all"
                : "Select all"}
            </button>
            <span>·</span>
            <span>
              Showing {Math.min(visibleCount, filtered.length)} of {filtered.length} recipe
              {filtered.length !== 1 ? "s" : ""}
            </span>
          </div>
        )}

        {/* Empty state */}
        {filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <BookOpen className="w-12 h-12 text-muted-foreground/40 mb-4" />
            <p className="text-lg font-medium text-muted-foreground">No recipes found</p>
            {hasActiveFilters && (
              <Button variant="ghost" className="mt-2" onClick={clearFilters}>
                Clear filters
              </Button>
            )}
          </div>
        )}

        {/* Grid view */}
        {viewMode === "grid" && filtered.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filtered.slice(0, visibleCount).map((recipe) => (
              <div
                key={recipe.id}
                className={cn(
                  "relative group rounded-2xl border bg-card transition-shadow hover:shadow-md cursor-pointer",
                  selected.has(recipe.id) && "ring-2 ring-primary"
                )}
              >
                {/* Checkbox */}
                <button
                  className="absolute top-2 left-2 z-10 p-1 rounded-lg bg-white/80 backdrop-blur-sm border border-border/50 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleSelect(recipe.id);
                  }}
                >
                  {selected.has(recipe.id) ? (
                    <CheckSquare className="w-4 h-4 text-primary" />
                  ) : (
                    <Square className="w-4 h-4 text-muted-foreground" />
                  )}
                </button>
                {selected.has(recipe.id) && (
                  <button
                    className="absolute top-2 left-2 z-10 p-1 rounded-lg bg-white/80 backdrop-blur-sm border border-primary/40"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleSelect(recipe.id);
                    }}
                  >
                    <CheckSquare className="w-4 h-4 text-primary" />
                  </button>
                )}
                <div
                  className="p-4"
                  onClick={() => navigate(`/recipe/${recipe.id}/edit`)}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="font-semibold text-foreground text-sm leading-tight line-clamp-2">
                      {recipe.title}
                    </h3>
                  </div>
                  <div className="flex flex-wrap gap-1 mb-3">
                    <span
                      className={cn(
                        "text-xs px-2 py-0.5 rounded-full border",
                        STATUS_COLORS[recipe.validationStatus] ?? STATUS_COLORS.unreviewed
                      )}
                    >
                      {STATUS_LABELS[recipe.validationStatus] ?? recipe.validationStatus}
                    </span>
                    {recipe.category && (
                      <span className="text-xs px-2 py-0.5 rounded-full border bg-blue-50 text-blue-700 border-blue-200">
                        {recipe.category}
                      </span>
                    )}
                    {recipe.potentialDuplicateOfId && (
                      <span className="text-xs px-2 py-0.5 rounded-full border bg-amber-50 text-amber-700 border-amber-200">
                        Duplicate?
                      </span>
                    )}
                    {calorieMap.has(recipe.id) ? (
                      <span className="text-xs px-2 py-0.5 rounded-full border bg-orange-50 text-orange-700 border-orange-200">
                        ~{calorieMap.get(recipe.id)} cal/serving
                      </span>
                    ) : (
                      <span className="text-xs px-2 py-0.5 rounded-full border border-dashed text-muted-foreground/50">
                        no nutrition
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground space-y-0.5">
                    {recipe.authorSource && (
                      <p className="truncate">By {recipe.authorSource}</p>
                    )}
                    {recipe.cuisine && <p>{recipe.cuisine}</p>}
                    {(recipe.ingredients?.length ?? 0) > 0 && (
                      <p>{recipe.ingredients!.length} ingredient{recipe.ingredients!.length !== 1 ? "s" : ""}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Table view */}
        {viewMode === "table" && filtered.length > 0 && (
          <div className="rounded-2xl border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 border-b">
                  <tr>
                    <th className="w-10 px-4 py-3">
                      <button onClick={toggleSelectAll}>
                        {selected.size === filtered.length && filtered.length > 0 ? (
                          <CheckSquare className="w-4 h-4 text-primary" />
                        ) : (
                          <Square className="w-4 h-4 text-muted-foreground" />
                        )}
                      </button>
                    </th>
                    <th className="text-left px-4 py-3 font-medium">Title</th>
                    <th className="text-left px-4 py-3 font-medium hidden sm:table-cell">
                      Category
                    </th>
                    <th className="text-left px-4 py-3 font-medium hidden md:table-cell">
                      Cuisine
                    </th>
                    <th className="text-left px-4 py-3 font-medium hidden lg:table-cell">
                      Author
                    </th>
                    <th className="text-left px-4 py-3 font-medium">Status</th>
                    <th className="text-left px-4 py-3 font-medium hidden xl:table-cell">
                      Ingredients
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filtered.slice(0, visibleCount).map((recipe) => (
                    <tr
                      key={recipe.id}
                      className={cn(
                        "hover:bg-muted/30 transition-colors cursor-pointer",
                        selected.has(recipe.id) && "bg-primary/5"
                      )}
                    >
                      <td className="px-4 py-3">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleSelect(recipe.id);
                          }}
                        >
                          {selected.has(recipe.id) ? (
                            <CheckSquare className="w-4 h-4 text-primary" />
                          ) : (
                            <Square className="w-4 h-4 text-muted-foreground" />
                          )}
                        </button>
                      </td>
                      <td
                        className="px-4 py-3 font-medium max-w-[200px] truncate"
                        onClick={() => navigate(`/recipe/${recipe.id}/edit`)}
                      >
                        {recipe.title}
                      </td>
                      <td
                        className="px-4 py-3 text-muted-foreground hidden sm:table-cell"
                        onClick={() => navigate(`/recipe/${recipe.id}/edit`)}
                      >
                        {recipe.category ?? "—"}
                      </td>
                      <td
                        className="px-4 py-3 text-muted-foreground hidden md:table-cell"
                        onClick={() => navigate(`/recipe/${recipe.id}/edit`)}
                      >
                        {recipe.cuisine ?? "—"}
                      </td>
                      <td
                        className="px-4 py-3 text-muted-foreground hidden lg:table-cell max-w-[150px] truncate"
                        onClick={() => navigate(`/recipe/${recipe.id}/edit`)}
                      >
                        {recipe.authorSource ?? "—"}
                      </td>
                      <td
                        className="px-4 py-3"
                        onClick={() => navigate(`/recipe/${recipe.id}/edit`)}
                      >
                        <div className="flex flex-wrap gap-1">
                          <span
                            className={cn(
                              "text-xs px-2 py-0.5 rounded-full border",
                              STATUS_COLORS[recipe.validationStatus] ??
                                STATUS_COLORS.unreviewed
                            )}
                          >
                            {STATUS_LABELS[recipe.validationStatus] ??
                              recipe.validationStatus}
                          </span>
                          {recipe.potentialDuplicateOfId && (
                            <span className="text-xs px-2 py-0.5 rounded-full border bg-amber-50 text-amber-700 border-amber-200">
                              Duplicate?
                            </span>
                          )}
                        </div>
                      </td>
                      <td
                        className="px-4 py-3 text-muted-foreground hidden xl:table-cell"
                        onClick={() => navigate(`/recipe/${recipe.id}/edit`)}
                      >
                        {recipe.ingredients?.length ?? 0}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Load more */}
        {filtered.length > visibleCount && (
          <div className="flex flex-col items-center gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
            >
              Load more ({filtered.length - visibleCount} remaining)
            </Button>
            <button
              type="button"
              onClick={() => setVisibleCount(filtered.length)}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              Show all {filtered.length}
            </button>
          </div>
        )}
      </div>

      {/* Bulk delete confirmation dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selected.size} recipe{selected.size !== 1 ? "s" : ""}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the selected recipes and all their ingredients.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleBulkDelete}
              disabled={bulkDeleteMut.isPending}
            >
              {bulkDeleteMut.isPending ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
